import { QuickstartTheme } from '../../types/theme';
import { ResolvedDeviceFallbackConfig } from '../../types/deviceFallbackTheme';
import { ThemeLogo } from '../ThemeLogo';
import { FallbackStageMedia } from './FallbackStageMedia';
import { SweepingQrRim } from './SweepingQrRim';
import { useHandoffQrCode } from './useHandoffQrCode';
import { withAlpha } from './fallbackColor';
import {
  DESKTOP_MEDIA_ZOOM,
  DESKTOP_PX,
  MEDIA_PANEL_WIDTH,
  PANEL_EDGE_ALPHA,
  PANEL_TINT_ALPHA,
  QR_CARD_COLOR,
  QR_CARD_PX,
  SCRIM_BLEED_PX,
  SCRIM_OVERHANG,
  SCRIM_WIDTH,
  buildCopyScrim,
  buildCopyScrimMask,
  desktopBlur,
  desktopPx,
  desktopScaled,
} from './desktopFallbackScale';

interface DesktopQrFallbackProps {
  theme: QuickstartTheme;
  config: ResolvedDeviceFallbackConfig;
  persistentId: string | undefined;
  originDeviceType: 'ios' | 'android' | 'desktop';
}

/**
 * Shown when a mobile-locked campaign is opened on a computer.
 *
 * Split stage: campaign media and the story on the left, scan station on the right.
 *
 * The stage fills the window rather than holding the artboard's 16:9, which would band the
 * top and bottom with flat stage colour on a taller window. It sizes off the viewport, not
 * off its parent, so a host that lays out shorter than the window can't clip the stage and
 * leave the page background showing beneath it.
 *
 * No "handoff requested" analytic on QR render — there is no scan signal, and treating
 * display as a request made the funnel incomparable to email (which fires after send).
 * Device Fallback Viewed already covers the screen impression.
 */
export const DesktopQrFallback = ({ theme, config, persistentId, originDeviceType }: DesktopQrFallbackProps) => {
  const { qrDataUrl } = useHandoffQrCode({
    persistentId,
    originDeviceType,
    backgroundColor: QR_CARD_COLOR,
  });
  const scrimBleed = desktopPx(SCRIM_BLEED_PX);
  const scrimMask = buildCopyScrimMask(scrimBleed);

  return (
    <div
      className='relative flex min-h-screen w-full items-center justify-center overflow-hidden font-["Inter"]'
      style={{ backgroundColor: config.stageColor }}
    >
      <div
        className='relative shrink-0 overflow-hidden'
        style={{
          // The window, whatever shape it is. Holding the stage to 16:9 instead left a bar
          // of flat stage colour above and below the campaign on any window taller than the
          // artboard, which reads as a gap rather than as framing. The composition is
          // anchored top and bottom — logos up, copy down — so spare height opens up between
          // them instead of stranding the picture in the middle of the screen.
          width: '100vw',
          height: '100vh',
          // Type still tracks the artboard rather than the window: whichever dimension is
          // binding sets the scale, so a wide, short window scales the composition down
          // rather than letting the copy outgrow its panel.
          ['--stage-w' as string]: 'min(100vw, calc(100vh * 16 / 9))',
        }}
      >
        {/* The campaign, across the whole stage rather than only the story side, so the two
          halves are one photograph with a pane over half of it rather than two pasted
          rectangles. */}
        <FallbackStageMedia
          theme={theme}
          blur={desktopBlur(config.backgroundBlurPx)}
          zoom={DESKTOP_MEDIA_ZOOM}
        />

        {/* No flat pass of stage colour over the campaign. One used to sit here, to hold the
          screen to Playcast's purple whatever the game's palette was, and review read it for
          exactly what it was: the video looking darker than it should, as though something
          were laid over it. Tinting belongs where it earns its keep — the scan panel's glass
          and the wedge under the copy — not across a picture the campaign paid for. */}

        {/* The scan panel, as glass over the same photograph: a second copy of the media
          blurred harder and tinted, clipped to the right of the split. Compositing it this
          way rather than with backdrop-filter keeps it identical in browsers that decline
          backdrop filters, where the panel would otherwise collapse to a flat tint. */}
        <div
          className='pointer-events-none absolute inset-0'
          style={{ clipPath: `inset(0 0 0 ${MEDIA_PANEL_WIDTH})` }}
        >
          {/* Same zoom as the layer below, or the clip would reveal a frame that no longer
            lines up with the one it is cut out of. */}
          <FallbackStageMedia
            theme={theme}
            blur={desktopBlur(config.panelBlurPx)}
            zoom={DESKTOP_MEDIA_ZOOM}
          />
          <div
            className='absolute inset-0'
            style={{ backgroundColor: withAlpha(config.stageColor, PANEL_TINT_ALPHA) }}
          />
        </div>

        {/* The join, drawn rather than left to chance: without it the glass edge reads as a
          compression artefact instead of a seam. */}
        <div
          className='pointer-events-none absolute inset-y-0'
          style={{
            left: MEDIA_PANEL_WIDTH,
            width: '1px',
            backgroundColor: withAlpha(config.accentColor, PANEL_EDGE_ALPHA),
          }}
        />

        <div className='relative flex h-full w-full'>
          {/* Story side: brand stack pinned to the top, copy pushed to the foot. */}
          <div
            className='relative flex h-full flex-col overflow-hidden'
            style={{ width: MEDIA_PANEL_WIDTH }}
          >
            {/* items-center sits the pair on a shared vertical axis rather than a shared
              left edge, and the stack takes the game logo's width so the studio mark
              centres over it rather than over the panel. */}
            <div
              className='flex min-w-0 max-w-full flex-col items-center'
              style={{
                padding: `${desktopPx(DESKTOP_PX.logoStackTop)} ${desktopPx(DESKTOP_PX.gutter)} 0`,
                gap: desktopPx(DESKTOP_PX.logoGap),
                alignSelf: 'flex-start',
              }}
            >
              <ThemeLogo kind='studio' theme={theme} style={{ height: desktopScaled(config.desktopStudioLogoScale) }} />
              <ThemeLogo kind='game' theme={theme} style={{ height: desktopScaled(config.desktopGameLogoScale) }} />
            </div>

            <div className='flex min-h-0 flex-1 flex-col justify-end'>
              <div className='relative w-full'>
                {/* The wedge under the copy, sized by the copy rather than by a guess: it is
                  pinned to this block, so it is exactly as tall as the words however they
                  wrap. It covers the block and no more — reaching above it only bought a
                  band of flat purple over the headline. The gradient carries the
                  left-to-right ramp and the mask decides how far up it reaches, which keeps
                  the two axes independent — a vertical fade stacked on top would blend as a
                  second veil and darken the whole panel rather than shaping this one. */}
                <div
                  aria-hidden
                  className='pointer-events-none absolute'
                  style={{
                    // Bled past the stage's left and bottom edges so the blur's own falloff
                    // is clipped away rather than eating the corner. The extra width is the
                    // bleed exactly, so the right edge — where the ramp has already given
                    // out — stays where it was.
                    //
                    // The top starts above the block instead of on it, which buys the fade a
                    // longer approach; the mask holds its landing point regardless.
                    top: `calc(-1 * ${SCRIM_OVERHANG})`,
                    left: `calc(-1 * ${scrimBleed})`,
                    bottom: `calc(-1 * ${scrimBleed})`,
                    width: `calc(${SCRIM_WIDTH} + ${scrimBleed})`,
                    background: buildCopyScrim(config.stageColor),
                    maskImage: scrimMask,
                    WebkitMaskImage: scrimMask,
                    filter: `blur(${desktopPx(DESKTOP_PX.scrimBlur)})`,
                  }}
                />

                <div
                  className='relative'
                  style={{
                    padding: `${desktopPx(DESKTOP_PX.copyTop)} ${desktopPx(DESKTOP_PX.gutter)} ${desktopPx(DESKTOP_PX.copyBottom)}`,
                    maxWidth: desktopPx(DESKTOP_PX.copyMaxWidth),
                  }}
                >
                  <h1
                    className='font-bold leading-tight'
                    style={{ color: config.qrRimStartColor, fontSize: desktopPx(DESKTOP_PX.headline) }}
                  >
                    {config.desktopHeadline}
                  </h1>
                  <p
                    style={{
                      marginTop: desktopPx(DESKTOP_PX.copyGap),
                      color: config.copyColor,
                      fontSize: desktopPx(DESKTOP_PX.subcopy),
                      lineHeight: 1.45,
                    }}
                  >
                    {config.desktopSubcopy}
                  </p>
                  <div
                    className='flex items-end'
                    style={{
                      gap: desktopPx(DESKTOP_PX.poweredByGap),
                      marginTop: desktopPx(DESKTOP_PX.poweredByOffset),
                      // Holds the old footprint so the headline and subcopy above stay put.
                      height: desktopPx(DESKTOP_PX.poweredBySlot),
                    }}
                  >
                    <ThemeLogo kind='poweredBy' theme={theme} style={{ height: desktopPx(DESKTOP_PX.poweredBy) }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scan station. The glass behind it is painted at stage level so it can span the
            full height; this layer only positions the code and its caption. */}
          <div
            className='relative flex flex-1 flex-col items-center justify-center'
            style={{
              gap: desktopPx(DESKTOP_PX.qrCaptionGap),
              padding: `${desktopPx(DESKTOP_PX.scanPanelPaddingY)} ${desktopPx(DESKTOP_PX.scanPanelPaddingX)}`,
            }}
          >
            <SweepingQrRim startColor={config.qrRimStartColor} endColor={config.qrRimEndColor}>
              <div
                className='flex items-center justify-center'
                style={{
                  width: desktopPx(QR_CARD_PX),
                  height: desktopPx(QR_CARD_PX),
                  padding: desktopPx(DESKTOP_PX.qrCardPadding),
                  borderRadius: desktopPx(DESKTOP_PX.qrCardRadius),
                  backgroundColor: QR_CARD_COLOR,
                }}
              >
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt='QR code to continue on your phone' className='h-full w-full object-contain' />
                ) : (
                  <div className='h-full w-full animate-pulse rounded-lg bg-neutral-200' />
                )}
              </div>
            </SweepingQrRim>

            {/* Semibold — heavier than body copy, lighter than the headline. */}
            <p
              className='relative font-semibold'
              style={{ color: config.qrRimStartColor, fontSize: desktopPx(DESKTOP_PX.qrCaption) }}
            >
              {config.desktopQrCaption}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
